import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AccordionDetails,
    FormControl,
    Grid,
    InputBaseComponentProps,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CostIcon from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/CostIcon';
import HypothesisValue from './HypothesisValue';
import HypothesisDate from './HypothesisDate';
import { StyBlock } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/PayConstantsEntryForm/helpers/StyledComponents';
import { CostPerYearT } from '../../../GVTData/helpers/hooks/gvtData';
import { StyAccordionCost, StyAccordionSummaryCost } from './TotalCost';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const StyFormControl = styled(FormControl)(({ theme }) => ({
    stroke: 'primary',
    '& .MuiInputBase-root': {
        backgroundColor: 'white',
    },
    '& .MuiInputBase-input': {
        fontSize: '12px',
        padding: '12px 14px',
    },
    '& .Mui-disabled': {
        color: theme.palette.black, // text color
        backgroundColor: 'transparent',
        fontSize: '12px',
    },
    width: '100%',
    height: 'fit-content',
}));

const StySelect = styled(Select)(() => ({
    stroke: 'primary',
    fontSize: '12px',
}));

type FreeHypothesisEntryFormProps = {
    isLoading: boolean;
    title: string;
    isHrSimulationResponseFetching: boolean;
    value: number | undefined;
    date: string | undefined;
    inputProps: InputBaseComponentProps;
    totalCost: number;
    costPerYear: CostPerYearT;
};

const IndexRaiseHypothesisEntryForm = ({
    title,
    value,
    date,
    inputProps,
    isHrSimulationResponseFetching,
    totalCost,
    costPerYear,
    isLoading,
}: FreeHypothesisEntryFormProps): React.ReactElement => {
    const { t } = useTranslation();

    const hypothesisType =
        'humanResources.simulation.freeHypothesis.indexRaise.hypothesisLabel';
    const agentClassification =
        'humanResources.simulation.freeHypothesis.indexRaise.agentClassification';

    return (
        <StyBlock>
            <Typography variant="h4" sx={{ fontSize: '16px' }}>
                {t(title)}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <StyFormControl>
                        <Typography variant="body1" sx={{ fontSize: '10px' }}>
                            {t(
                                'humanResources.simulation.freeHypothesis.freeHypothesisType',
                            )}
                        </Typography>

                        <StySelect
                            value={t(hypothesisType)}
                            onChange={() => {}}
                        >
                            <MenuItem value={t(hypothesisType)}>
                                {t(hypothesisType)}
                            </MenuItem>
                        </StySelect>
                    </StyFormControl>
                </Grid>
                <Grid item xs={6}>
                    <StyFormControl>
                        <Typography variant="body1" sx={{ fontSize: '10px' }}>
                            {t(
                                'humanResources.simulation.freeHypothesis.indexRaise.agentClassificationTitle',
                            )}
                        </Typography>
                        <Select
                            value={t(agentClassification)}
                            onChange={() => {}}
                        >
                            <MenuItem value={t(agentClassification)}>
                                {t(agentClassification)}
                            </MenuItem>
                        </Select>
                    </StyFormControl>
                </Grid>

                <Grid item xs={4}>
                    <HypothesisValue
                        futureValue={value}
                        typeValue="number"
                        onChange={() => {}}
                        inputProps={inputProps}
                        isLoading={isLoading}
                        disabled
                    />
                </Grid>
                <Grid item xs={4}>
                    <HypothesisDate
                        applicationDate={date}
                        onDateChange={() => {}}
                        isLoading={isLoading}
                        disabled
                    />
                </Grid>
                <Grid item xs={4} justifyContent="space-around">
                    <>
                        <Typography
                            variant="body1"
                            sx={{ fontSize: '12px', pt: 2 }}
                        >
                            {t(
                                'humanResources.simulation.freeHypothesis.indexRaise.raiseCost',
                            )}
                        </Typography>
                        <StyAccordionCost>
                            <StyAccordionSummaryCost
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <CostIcon
                                    value={totalCost}
                                    isLoading={
                                        isLoading ||
                                        isHrSimulationResponseFetching
                                    }
                                />
                            </StyAccordionSummaryCost>
                            <AccordionDetails
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                {costPerYear &&
                                    Object.entries(costPerYear).map(
                                        ([year, cost]) => (
                                            <CostIcon
                                                key={year}
                                                value={cost}
                                                isLoading={isLoading}
                                                year={Number(year)}
                                            />
                                        ),
                                    )}
                            </AccordionDetails>
                        </StyAccordionCost>
                    </>
                </Grid>
            </Grid>
        </StyBlock>
    );
};

export default memo(IndexRaiseHypothesisEntryForm);
